import { exception } from 'circe/logger';
import { getCurrentAuthHeader } from 'domain/fuse/auth/utils';
import { morphShipmentToUI } from 'domain/order/utils';
import { morphOriginalResponseUsingState } from 'domain/shipment/view/utils';
import {
  all, takeLatest, call, select, put, takeEvery,
} from 'redux-saga/effects';
import * as I18n from 'circe/I18n';
import { show as showNotification } from 'circe/notifications/actions';
import { reassigned } from 'domain/operations/dashboard/reassignment/actions';
import { SHIPMENT_STATUS } from 'domain/shipment/constants';
import { change, changeData } from 'circe/queryListing/actions';
import { getResults as getResultsListing } from 'circe/queryListing/selectors';
import { updateStatus } from 'domain/operations/dashboard/reassignment/api';
import moment from 'moment';
import * as api from 'domain/operations/dashboard/shipmentEscalation/api';
import { makeFetchPayloadFromFilters } from 'domain/operations/dashboard/shipmentEscalation/utils';
import {
  fetch,
  fetchNext,
  fetchPrev,
  fetching,
  fetched,
  updateShipment,
  cancel,
  cancelled,
  updateRecipient,
  updatedRecipient,
} from 'domain/operations/dashboard/shipmentEscalation/actions';

import {
  getNextCursor,
  getHasNext,
  getHasPrev,
  getPrevCursor,
  getResults,
} from 'domain/operations/dashboard/shipmentEscalation/selectors';
import { REDUCER_NAME } from 'domain/operations/dashboard/shipmentEscalation/constants';

function* doFetch(context, { payload }) {
  try {
    const state = yield select();

    const { filters } = payload;

    const headers = yield call(getCurrentAuthHeader, state);

    const requestFilters = yield call(makeFetchPayloadFromFilters, filters);

    const resp = yield call(api.fetch, requestFilters, headers);

    const results = resp.results
      .map((shipment) => {
        if (!shipment.driver) {
          shipment.driver = {
            accessorials: [],
            company: SYSTEM_DOMAIN,
            id: '-',
            name: '-',
            picture: '',
            rating: 0,
            shipments: 0,
          };
        }

        if (!shipment.vehicle) {
          shipment.vehicle = {
            id: '-',
            mocked: true,
            vehicleType: 'FLAT_BED',
            maxWeight: 35000,
            size: {
              length: 22,
              width: 2.4,
              height: 4,
            },
            capacity: {
              length: 22,
              width: 2.4,
              height: 4,
            },
            tempRange: null,
          };
        }

        if (!shipment.pricing) {
          shipment.pricing = {
            invoiceId: '5d911c29-59f1-409e-a7dc-ae7f0b5310d9',
            shipmentId: shipment.shipmentId,
            invoiceType: 'Sales',
            salesInvoice: {
              lineItems: [
                {
                  name: 'Fare',
                  id: '0',
                  tier: 'tier',
                  quantity: 0,
                  unitPrice: 0,
                  flatPrice: 0,
                  discounts: [],
                  markups: [],
                  taxes: [],
                  subTotal: 0,
                  itemGrandTotal: 99.03,
                },
                {
                  name: 'Assurance Recovery Fees',
                  id: '3',
                  tier: 'tier_1',
                  quantity: 0,
                  unitPrice: 0,
                  flatPrice: 0,
                  discounts: [],
                  markups: [],
                  taxes: [],
                  subTotal: 0,
                  itemGrandTotal: 1.95,
                },
                {
                  name: 'Fees, Government Fees, and Other',
                  id: '4',
                  tier: 'tier_1',
                  quantity: 0,
                  unitPrice: 0,
                  flatPrice: 0,
                  discounts: [],
                  markups: [],
                  taxes: [],
                  subTotal: 0,
                  itemGrandTotal: 0.5,
                },
                {
                  name: 'VAT',
                  id: '5',
                  tier: 'tier_1',
                  quantity: 0,
                  unitPrice: 0,
                  flatPrice: 0,
                  discounts: [],
                  markups: [],
                  taxes: [],
                  subTotal: 0,
                  itemGrandTotal: 5.07,
                },
              ],
              adjustment: [],
              total: 106.54,
              currency: 'SAR',
            },
          };
        }
        return shipment;
      })
      .map((shipment) => morphOriginalResponseUsingState(shipment, state));
    yield put(
      fetched(results, {
        nextCursor: resp.nextPage && resp.nextPage.hasResults ? resp.nextPage.cursor : null,
        prevCursor: resp.prevPage && resp.prevPage.hasResults ? resp.prevPage.cursor : null,
        filters,
      }),
    );
  } catch (e) {
    yield put(fetched());
    exception(e);
  }
}

export function* doFetchNext(context) {
  const state = yield select();

  const hasNextPage = yield call(getHasNext, state);

  if (!hasNextPage) {
    return;
  }

  const cursor = yield call(getNextCursor, state);

  yield put(fetching());

  yield call(doFetch, context, fetch({ cursor }));
}

export function* doFetchPrev(context) {
  const state = yield select();

  const hasPrevPage = yield call(getHasPrev, state);

  if (!hasPrevPage) {
    return;
  }

  const cursor = yield call(getPrevCursor, state);

  yield put(fetching());

  yield call(doFetch, context, fetch({ cursor }));
}

export function* doReassigned(context, { payload }) {
  try {
    const { shipmentId, driver, vehicle } = payload;

    const state = yield select();

    const shipments = yield call(getResults, state);

    const shipment = shipments.find((shipment) => shipment.shipmentId === shipmentId);

    yield put(
      showNotification(
        I18n.str('shipmentReassignedSuccessfully', 'Shipment has been reassigned successfully.'),
        'success',
      ),
    );

    yield put(
      changeData(
        REDUCER_NAME,
        getResultsListing(state, REDUCER_NAME).map((shipment) => {
          if (shipment.shipmentId === shipmentId) {
            return {
              ...shipment,
              reassigned: true,
            };
          }
          return shipment;
        }),
      ),
    );
  } catch (e) {
    yield put(
      showNotification(I18n.str('shipmentReassignFailed', 'Shipment Reassign Failed.'), 'error'),
    );

    exception(e);
  }
}

function* doCancel({ payload }) {
  const { invoiceId, shipmentId, shipperId } = payload;
  try {
    const state = yield select();

    const headers = yield call(getCurrentAuthHeader, state);

    yield put(
      changeData(
        REDUCER_NAME,
        getResultsListing(state, REDUCER_NAME).map((shipment) => {
          if (shipment.shipmentId === shipmentId) {
            return {
              ...shipment,
              isCancelling: true,
            };
          }
          return shipment;
        }),
      ),
    );

    const resp = yield call(api.cancel, shipmentId, headers);

    yield call(api.cancelConfirm, resp.invoiceId, shipmentId, headers);
    yield put(
      changeData(
        REDUCER_NAME,
        getResultsListing(state, REDUCER_NAME).map((shipment) => {
          if (shipment.shipmentId === shipmentId) {
            return {
              ...shipment,
              isCancelling: false,
              canceled: true,
              status: 'CANCELLED',
            };
          }
          return shipment;
        }),
      ),
    );

    yield put(
      showNotification(
        I18n.str('shipmentCancelSuccess', 'Shipment has been cancelled successfully.'),
        'success',
      ),
    );
    yield put(
      cancelled({
        invoiceId,
        shipmentId,
        shipperId,
      }),
    );
  } catch (e) {
    yield put(
      showNotification(I18n.str('shipmentCancelFailed', 'Shipment cancellation failed.'), 'error'),
    );
    exception(e);
    yield put(
      cancelled({
        invoiceId,
        shipmentId,
        shipperId,
      }),
    );
  }
}

function* doUpdateWaybill({ payload }) {
  try {
    const state = yield select();
    const headers = yield call(getCurrentAuthHeader, state);

    const { values } = payload;

    // if(values.status == "EXCEPTION"){ // TODO: buisness logic, big oppse
    //
    //   const data = {
    //     waybill: values.waybill,
    //     status: "UNASSIGNED",
    //     message: "",
    //   };
    //
    //   const resp = yield call(updateStatus, data, headers);
    //
    //   if(resp.responseCode == "SUCCESS"){
    //     throw new Error(resp.message);
    //   }
    // }

    const req = {
      salesOrderNumber: values.salesOrderNumber,
      salesOrderLineNumber: values.salesOrderLineNumber,
      itemNumber: values.itemNumber,
      dropoff: {
        address: values.toAddress[0].formatted_address,
        arrivalWindow: {
          begin: moment(values.deliveryDate).toISOString(),
          end: moment(values.deliveryDate)
            .add(1, 'days')
            .toISOString(),
          excludeBegin: true,
          excludeEnd: true,
        },
        coordinate: {
          latitude:
            typeof values.toAddress[0].geometry.location.lat === 'function'
              ? values.toAddress[0].geometry.location.lat()
              : values.toAddress[0].geometry.location.lat,
          longitude:
            typeof values.toAddress[0].geometry.location.lng === 'function'
              ? values.toAddress[0].geometry.location.lng()
              : values.toAddress[0].geometry.location.lng,
        },
        description: values.toAddress[0].formatted_address,
        duration: null,
      },
      receiverContact: {
        name: values.contact,
        phone: values.mobile,
        email: values.email,
      },
      shipmentsToAssign: [values.shipmentId],
    };
    const resp = yield call(api.updateRecipient, req, headers);

    if (!resp.success) {
      throw new Error(resp.message);
    }

    yield put(
      showNotification(
        `${I18n.str('waybillUpdatedSuccessfully', 'Waybill updated successfully.')}`,
        'success',
      ),
    );

    yield put(updatedRecipient());
  } catch (e) {
    yield put(showNotification(I18n.str('waybillUpdateFailed', 'Waybill update failed.'), 'error'));
    exception(e);
    yield put(updatedRecipient());
  }
}

export default function* (context) {
  yield all([
    takeLatest(fetch().type, doFetch, context),
    takeLatest(fetchNext().type, doFetchNext, context),
    takeLatest(fetchPrev().type, doFetchPrev, context),
    takeEvery(reassigned().type, doReassigned, context),
    takeEvery(updateRecipient().type, doUpdateWaybill),
    takeEvery(cancel().type, doCancel),
  ]);
}
